import { ApiResponse } from './userApi';
import api from './apiClient';

export interface SupervisedContractor {
  _id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  cedula: string;
  email?: string;
  phone?: string;
  role?: string;
  ordenPatronal?: string;
  polizaINS?: {
    number: string;
    expiryDate: string;
  };
  status: string;
  company: {
    _id: string;
    name: string;
    industry?: string;
    userCount?: number;
  };
  certifications?: {
    name: string;
    issuedDate: string;
    expiryDate: string;
    status: string;
    _id: string;
  }[];
  courses?: {
    courseId: string;
    courseName: string;
    completedDate: string;
    score: number;
    status: string;
    _id: string;
  }[];
  supervisor?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt?: string;
  updatedAt?: string;
  lastAccess?: string;
}

export interface SupervisedContractorStats {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  expiringDocuments: number;
  companiesCount: number;
  byCompany: {
    _id: string;
    name: string;
    count: number;
    active: number;
    inactive: number;
  }[];
}

export interface SupervisedContractorFilters {
  page?: number;
  limit?: number;
  companyId?: string;
  status?: 'activo' | 'inactivo' | 'baja';
  search?: string;
  expiringDocs?: boolean;
}

export interface SupervisedContractorResponse {
  success: boolean;
  data: {
    contractors: SupervisedContractor[];
    summary: {
      totalContractors: number;
      activeContractors: number;
      inactiveContractors: number;
      expiringDocuments: number;
      companiesSupervised: number;
      contractorsByCompany: {
        _id: string;
        companyId: string;
        companyName: string;
        totalContractors: number;
        activeContractors: number;
      }[];
    };
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  message?: string;
}

export const supervisedContractorApi = {
  // Get all supervised contractors with filters
  getAll: async (filters?: SupervisedContractorFilters): Promise<SupervisedContractorResponse> => {
    return api.get('/contractors/supervised', filters as Record<string, any>);
  },

  // Get contractor by ID (if within supervised companies)
  getById: async (id: string): Promise<ApiResponse<SupervisedContractor>> => {
    return api.get(`/contractors/supervised/${id}`);
  },

  // Get statistics for supervised contractors
  getStats: async (): Promise<ApiResponse<SupervisedContractorStats>> => {
    return api.get('/contractors/supervised/stats');
  },

  // Get contractors by company (only supervised companies)
  getByCompany: async (companyId: string, filters?: Omit<SupervisedContractorFilters, 'companyId'>): Promise<SupervisedContractorResponse> => {
    return api.get('/contractors/supervised', { ...filters, companyId } as Record<string, any>);
  },

  // Get contractors with expiring documents
  getExpiringDocs: async (filters?: Omit<SupervisedContractorFilters, 'expiringDocs'>): Promise<SupervisedContractorResponse> => {
    return api.get('/contractors/supervised', { ...filters, expiringDocs: true } as Record<string, any>);
  },

  // Search contractors
  search: async (searchTerm: string, filters?: Omit<SupervisedContractorFilters, 'search'>): Promise<SupervisedContractorResponse> => {
    return api.get('/contractors/supervised', { ...filters, search: searchTerm } as Record<string, any>);
  }
};
